/**
 * EditChannelPage - Página de configuração/edição de canal
 *
 * Seções:
 * 1. Informações Básicas (nome do canal)
 * 2. Conexão (QR Code para gateway-whatsapp)
 * 3. Operadores do Canal
 * 4. Identificação de Operadores
 * 5. Configuração de Atribuição
 */

import React, { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useChannel, useUpdateChannel } from '../hooks';
import { Button } from '@/shared/components/ui/Button';
import { Breadcrumb } from '@/shared/components/ui/Breadcrumb';
import type { UpdateChannelRequest, DistributionConfig } from '@troia-v3/types';

// Sections
import { BasicInfoSection } from '../components/sections/BasicInfoSection';
import { ProviderConfigSection } from '../components/provider-configs';
import { OperatorIdentificationSection } from '../components/sections/OperatorIdentificationSection';
import { ExpirationConfigSection } from '../components/sections/ExpirationConfigSection';
import { TranscriptionConfigSection } from '../components/sections/TranscriptionConfigSection';
import { SatisfactionConfigSection } from '../components/sections/SatisfactionConfigSection';
import { OutOfHoursConfigSection } from '../components/sections/OutOfHoursConfigSection';
import { InactivityTransferConfigSection } from '../components/sections/InactivityTransferConfigSection';
import { AssignmentConfigSection } from '@/shared/components/assignment';
import { ContextUsersTab } from '@/shared/components/context-users';
import { Card, CardContent } from '@/shared/components/ui/Card';
import { Icon } from '@/shared/icons'

// Phase 1b: `channel.assignmentConfig` is `DistributionConfig` — no
// conversion helpers needed.

export const EditChannelPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Fetch channel data
  const { data: channel, isLoading } = useChannel(id!);
  const updateMutation = useUpdateChannel();

  // Form state
  const [formData, setFormData] = useState<Partial<UpdateChannelRequest>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Config específico do provider (ex.: email-resend) integra o MESMO botão
  // "Salvar Alterações" do header — não existe segundo botão de salvar.
  const [providerDirty, setProviderDirty] = useState(false);
  const providerSaveRef = useRef<(() => Promise<boolean>) | null>(null);
  const [savingProvider, setSavingProvider] = useState(false);
  const anyChanges = hasChanges || providerDirty;

  // Update form data when channel loads
  React.useEffect(() => {
    if (channel) {
      setFormData({
        name: channel.name,
        identifyUser: channel.identifyUser,
        expirationConfig: channel.expirationConfig,
        transcriptionConfig: channel.transcriptionConfig,
        satisfactionConfig: channel.satisfactionConfig,
        outOfHoursConfig: channel.outOfHoursConfig,
        inactivityTransferConfig: channel.inactivityTransferConfig,
        assignmentConfig: channel.assignmentConfig,
        status: channel.status,
      });
    }
  }, [channel]);

  // Handle form field changes
  const handleFieldChange = (field: keyof UpdateChannelRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Save unificado: campos da página (PUT /channels/:id) + config do
  // provider (PATCH próprio, registrado via providerSaveRef) num só botão.
  const handleSave = async () => {
    if (!id || !anyChanges) return;

    let ok = true;
    if (providerDirty && providerSaveRef.current) {
      setSavingProvider(true);
      try {
        ok = await providerSaveRef.current();
      } finally {
        setSavingProvider(false);
      }
      if (!ok) return; // o bloco do provider já mostrou o erro
    }

    try {
      if (hasChanges) {
        await updateMutation.mutateAsync({ id, data: formData });
        setHasChanges(false);
      }
      toast.success('Alterações salvas.');
    } catch (error: any) {
      console.error('❌ Erro ao atualizar canal:', error);
      toast.error('Erro ao salvar as alterações do canal.');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Icon name="loader" className="w-8 h-8 animate-spin" style={{ color: 'var(--theme-button-primary)' }} />
      </div>
    );
  }

  // Error state — só desmonta o form quando NÃO há dado em cache. Um refetch
  // em background que falha (ex.: backend reiniciando via nodemon) marca
  // isError=true mesmo com `channel` válido; desmontar aqui destruía a edição
  // em curso do usuário (verificado em browser 2026-07-13).
  if (!channel) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-lg" style={{ color: 'var(--theme-error-color)' }}>
          Erro ao carregar canal
        </p>
        <Button onClick={() => window.location.reload()} variant="secondary">
          Tentar Novamente
        </Button>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
      data-testid="edit-channel-page"
    >
      {/* Sticky Header — breadcrumb (nunca seta-voltar) + título + Salvar */}
      <div
        className="sticky top-0 z-10 shadow-sm"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderBottom: '1px solid var(--theme-card-border)',
        }}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-3 flex items-center justify-between gap-4">
          {/* Breadcrumb shared (F25–F31) — única linha da toolbar, sem título 2xl */}
          <Breadcrumb
            items={[
              { label: 'Configurações', onNavigate: () => navigate('/settings'), testid: 'edit-channel-breadcrumb-settings' },
              { label: 'Canais', onNavigate: () => navigate('/settings/channels'), testid: 'edit-channel-breadcrumb-channels' },
              { label: channel.name, testid: 'edit-channel-breadcrumb-current' },
            ]}
            mobileLegend="Configurações · Canais"
            data-testid="edit-channel-breadcrumb"
          />

          {/* Ações */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {anyChanges && (
              <span
                className="text-sm hidden sm:inline"
                style={{ color: 'var(--theme-warning-color)' }}
              >
                Alterações não salvas
              </span>
            )}
            <Button
              onClick={handleSave}
              disabled={!anyChanges || updateMutation.isPending || savingProvider}
              data-testid="edit-channel-save-btn"
            >
              {updateMutation.isPending || savingProvider ? (
                <>
                  <Icon name="loader" className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Icon name="save" className="w-4 h-4 mr-2" />
                  Salvar
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        <div className="space-y-6">
          {/* 1. Basic Info Section */}
          <BasicInfoSection
            channel={channel as any}
            formData={formData}
            onFieldChange={handleFieldChange}
          />

          {/* 2. Provider Config Section (despacha configs específicas do provider;
              salva pelo MESMO botão do header via onDirtyChange/saveRef) */}
          <ProviderConfigSection
            channel={channel as any}
            onDirtyChange={setProviderDirty}
            saveRef={providerSaveRef}
          />

          {/* 3. Operators Section (migrado para ContextUsersTab) */}
          <Card>
            <CardContent className="px-4 pb-4 pt-4">
              <ContextUsersTab contextRef={{ kind: 'channel', id: channel.id }} />
            </CardContent>
          </Card>

          {/* 4. Operator Identification Section */}
          <OperatorIdentificationSection
            channel={channel as any}
            formData={formData}
            onFieldChange={handleFieldChange}
          />

          {/* 5. Expiration Config Section */}
          <ExpirationConfigSection
            channel={channel as any}
            formData={formData}
            onFieldChange={handleFieldChange}
          />

          {/* 6. Transcription Config Section (Transcrição Automática) */}
          <TranscriptionConfigSection
            channel={channel as any}
            formData={formData}
            onFieldChange={handleFieldChange}
          />

          {/* 7. Satisfaction Config Section (CSAT) */}
          <SatisfactionConfigSection
            channel={channel as any}
            formData={formData}
            onFieldChange={handleFieldChange}
          />

          {/* 8. Out of Hours Config Section */}
          <OutOfHoursConfigSection
            channel={channel as any}
            formData={formData}
            onFieldChange={handleFieldChange}
          />

          {/* 9. Distribution Config Section (Phase 1b) */}
          <AssignmentConfigSection
            config={
              (formData.assignmentConfig ?? channel.assignmentConfig) as DistributionConfig ?? {
                enabled: false,
                type: 'sequential',
              }
            }
            onChange={(config) => handleFieldChange('assignmentConfig', config)}
            resourceType="channel"
            resourceId={channel.id}
            variant="card"
          />

          {/* 10. Transferência automática quando o atendente não responde
               (logo depois da Distribuição — o destino "rodízio" depende dela) */}
          <InactivityTransferConfigSection
            channel={channel as any}
            formData={formData}
            onFieldChange={handleFieldChange}
          />

          {/* 5. Test Assignment Section - Comentado por enquanto */}
          {/* <TestAssignmentSection channelId={channel.id} /> */}
        </div>
      </div>
    </div>
  );
};

EditChannelPage.displayName = 'EditChannelPage';
